import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import VideoTutorialButton from "@/components/poshan/VideoTutorialButton";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import FloatingLabelInput from "@/components/mobile/FloatingLabelInput";

const CATEGORIES = [
  "BV+Primary",
  "BV+Primary+Upper Primary",
  "Upper Primary (6,7,8)",
  "BV+Primary+Upper Primary+High School",
  "High School (IX-X)",
];

export default function Register() {
  const { user, register, ready } = useAuth();
  const nav = useNavigate();
  // UDISE removed from registration (iter 94) — schools enter it on the
  // Setup page → School Identity section after first login. Until then the
  // backend assigns a unique PENDING-* placeholder so all per-school
  // endpoints keep working.
  const [form, setForm] = useState({ schoolName: "", schoolCategory: "BV+Primary", mobile: "", password: "", confirmPassword: "" });
  const [busy, setBusy] = useState(false);
  // iter129 — explicit acknowledgement that the platform is independent.
  // Register button stays disabled until the user ticks the box.
  const [acknowledged, setAcknowledged] = useState(false);

  if (!ready) return null;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.schoolName || !form.mobile || !form.password) { toast.error("Fill all required fields"); return; }
    if (form.mobile.length !== 10) { toast.error("Mobile Number must be exactly 10 digits"); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match"); return; }
    if (form.password.length < 4) { toast.error("Password must be 4+ characters"); return; }
    if (!acknowledged) { toast.error("Please tick the independent-platform acknowledgement to continue"); return; }
    setBusy(true);
    try {
      // Backend accepts blank udiseCode and generates a PENDING-* placeholder.
      // iter130 — also send the acknowledgement so it's persisted on the server
      // as an audit trail (timestamp + IP + User-Agent).
      const u = await register({ ...form, udiseCode: "", acknowledgeIndependent: true });
      toast.success(`Welcome, ${u.schoolName}`);
      nav("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-sand-50 px-4 py-12 text-ink-900 sm:px-6">
      <div className="mx-auto max-w-md">
        <div className="mb-8 text-center">
          <img src="/logo192.png" alt="MDM Sahayak logo" className="mx-auto h-16 w-16 rounded-xl shadow-sm" />
          <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">MDM Sahayak</h1>
        </div>

        {/* iter129 — prominent independence disclaimer above the registration form. */}
        <div className="mb-5">
          <DisclaimerBanner variant="compact" />
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="register-form">
          <h2 className="text-xl font-semibold flex items-center gap-2"><UserPlus className="h-5 w-5 text-clay" /> Create an account</h2>
          <div className="mt-5 space-y-4">
            {/* iter137 — floating-label inputs + h-12 touch targets for mobile ergonomics. */}
            <FloatingLabelInput
              id="name"
              label="School Name *"
              value={form.schoolName}
              onChange={(e) => set("schoolName", e.target.value)}
              testid="register-school-name-input"
            />
            <div>
              <Label className="mb-1 block text-xs font-bold uppercase tracking-wider text-brand-600">School Category *</Label>
              <Select value={form.schoolCategory} onValueChange={(v) => set("schoolCategory", v)}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="register-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <FloatingLabelInput
              id="mobile"
              label="Mobile Number *"
              inputMode="numeric"
              autoComplete="tel"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10}
              pattern="[0-9]{10}"
              testid="register-mobile-input"
            />
            <FloatingLabelInput
              id="pw"
              label="Password *"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              testid="register-password-input"
            />
            <FloatingLabelInput
              id="cpw"
              label="Confirm Password *"
              type="password"
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={(e) => set("confirmPassword", e.target.value)}
              testid="register-confirm-password-input"
            />
          </div>
          {/* iter129 — mandatory acknowledgement: visitor confirms they understand
              this is an independent private platform before submitting. */}
          <label
            className="mt-6 flex cursor-pointer items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs leading-relaxed text-ink-800 has-[:checked]:border-emerald-300 has-[:checked]:bg-emerald-50/70"
            data-testid="register-acknowledge-label"
          >
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-emerald-600"
              data-testid="register-acknowledge-checkbox"
              aria-label="I understand this is an independent private digital platform"
            />
            <span>
              <b className="font-semibold text-ink-900">I understand</b> this is an
              <b className="font-semibold text-ink-900"> independent private digital platform</b>{" "}
              and is not affiliated with any government authority or official department.
            </span>
          </label>
          <Button type="submit" disabled={busy || !acknowledged} className="mt-4 h-12 w-full bg-clay text-base font-bold hover:bg-clay/90 disabled:cursor-not-allowed disabled:opacity-60" data-testid="register-submit-btn">
            {busy ? "Creating account…" : "Register"}
          </Button>

          <p className="mt-4 text-center text-sm text-ink-700">
            Already registered? <Link to="/login" className="text-clay font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
          </p>
        </form>

        {/* Same Odia tutorial CTAs shown on Login — surface them on Register
            too so first-time users can watch BEFORE filling the form. */}
        <div className="mt-5 space-y-3" data-testid="register-tutorials">
          <VideoTutorialButton
            youtubeId="VWrMfzv-81M"
            titleOdia="🎬 ଭିଡିଓ ଟ୍ୟୁଟୋରିଆଲ୍ ଦେଖନ୍ତୁ"
            subtitleOdia="Account କିପରି ତିଆରି କରିବେ — Step by Step ଭିଡିଓ"
            dialogTitleOdia="MDM Sahayak — Account Creation"
            durationLabel="▶ Watch Now"
            variant="sunset"
            testId="register-video-tutorial"
          />
          <VideoTutorialButton
            youtubeId="T3Ym27t4Rhc"
            titleOdia="📊 MDM Bill · UC · Hand Receipts କିପରି ତିଆରି ହେବ"
            subtitleOdia="Portal ରେ ସମ୍ପୂର୍ଣ୍ଣ ପ୍ରକ୍ରିୟା — Live Demo"
            dialogTitleOdia="MDM Bill, UC & Hand Receipts on the Portal"
            durationLabel="▶ Full Walkthrough"
            variant="moss"
            testId="register-mdm-walkthrough"
          />
        </div>
      </div>
    </main>
  );
}
